// src/components/common/SparkNav.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface SparkNavProps {
  onLogout: () => void;
}

const SparkNav: React.FC<SparkNavProps> = ({ onLogout }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  // close if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        menuOpen &&
        !target.closest('.spark-nav__hamburger') &&
        !target.closest('.spark-nav__menu')
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <>
      {/* ───────── Hamburger ───────── */}
      <div
        className="spark-nav__hamburger"
        onClick={toggleMenu}
        style={{
          position: 'absolute',
          top: '10px',
          right: '20px',
          width: '45px',
          height: '45px',
          background: 'var(--color-surface-glass)',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '5px',
          zIndex: 4000,
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          backdropFilter: 'var(--blur-glass)',
          WebkitBackdropFilter: 'var(--blur-glass)',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-glow)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(1px)')}
        onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
      >
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            style={{
              width: '24px',
              height: '3px',
              backgroundColor: 'white',
              borderRadius: '2px',
              marginBottom: i < 2 ? '5px' : undefined,
            }}
          />
        ))}
      </div>

      {/* ───────── Dropdown ───────── */}
      {menuOpen && (
        <div
          className="spark-nav__menu"
          style={{
            position: 'fixed',
            top: '85px',
            right: '20px',
            background: 'var(--color-surface-glass)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 'var(--radius-sm)',
            zIndex: 4001,
            minWidth: '200px',
            maxHeight: 'calc(100vh - 85px)',
            overflowY: 'auto',
            animation: 'slideDown 0.3s ease-out forwards',
            boxShadow: 'var(--shadow-glow)',
            backdropFilter: 'var(--blur-glass)',
            WebkitBackdropFilter: 'var(--blur-glass)',
          }}
        >
          {[
            { to: '/dashboard', label: 'Dashboard' },
            { to: '/create-event', label: 'Create Event' },
            { to: '/create-event-from-template', label: 'See All Invitations' },
            { to: '/account', label: 'My Account' },
          ].map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setMenuOpen(false)}
              style={{
                display: 'block',
                padding: '12px 16px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                color: 'white',
                textDecoration: 'none',
                transition: 'background-color 0.2s',
                position: 'relative',
                zIndex: 4002,
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor =
                  'rgba(255, 255, 255, 0.1)')
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = 'transparent')
              }
            >
              {label}
            </Link>
          ))}

          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onLogout();
              setMenuOpen(false);
            }}
            style={{
              display: 'block',
              padding: '12px 16px',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              color: 'white',
              textDecoration: 'none',
              transition: 'background-color 0.2s',
              position: 'relative',
              zIndex: 4002,
              cursor: 'pointer',
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor =
                'rgba(255, 255, 255, 0.1)')
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = 'transparent')
            }
          >
            Log Out
          </a>
        </div>
      )}

      {/* ───────── Inline styles ───────── */}
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .spark-nav__menu,
        .spark-nav__menu a { pointer-events: auto !important; }

        /* ──── NEW: mobile contrast fix ──── */
        @media (max-width: 640px) {
          /* Mobile: only the dropdown panel gets the solid‑white glass treatment */
          .spark-nav__menu {
            background: rgba(255, 255, 255, 0.95) !important;
            backdrop-filter: blur(24px) !important;
            -webkit-backdrop-filter: blur(24px) !important;
          }

          /* High‑contrast gradient text for each link (unchanged) */
          .spark-nav__menu a {
            background-image: linear-gradient(90deg,#a855ff 0%,#f472b6 100%);
            -webkit-background-clip: text;
            color: transparent !important;
            font-weight: 700;
          }
        }
webkit-backdrop-filter: blur(24px) !important;
          }
          .spark-nav__menu a {
            background-image: linear-gradient(90deg,#a855ff 0%,#f472b6 100%);
            -webkit-background-clip: text;
            color: transparent !important;
            font-weight: 700;
          }
        }
      `}</style>
    </>
  );
};

export default SparkNav;
